/* eslint-disable camelcase */
import * as THREE from 'three'

import { getSeatMaterial, getLineMaterial } from '../utils/helper_functions'
import { drawLabel } from './drawLabel'
import { drawTableCircle } from './drawTableCircle'

// constants
import { COLORS, SEAT_STATUSES, SEAT_TYPES, OBJECT_TYPES } from '../constants'

import { drawRect } from '../utils/helper_functions/drawRect'
import { drawIcon } from '../utils/helper_functions/drawIcon'

export const drawTableSeats = (
  seatId,
  options,
  seats,
  materials,
  textGeometries,
  group
) => {
  const {
    scene,
    color,
    position,
    rotation,
    tierId,
    parent,
    center_x,
    center_y,
    scale,
    seat_circle_radius,
    events,
    sectionLabel,
    rowLabel,
    label,
    fonts,
    disabled = false,
    type = SEAT_TYPES.CHAIR,
    isExternalLink,
    seatExternalLink,
    enableTicketTypeAssign,
    tierColor,
    selectedTierId,
    sceneMeshes
  } = options

  let { opacity = 1 } = options

  const seatColor = enableTicketTypeAssign ? tierColor : COLORS.BOOKED

  position[0] -= center_x * scale
  position[1] += center_y * scale

  const radius = (seat_circle_radius || 14 - 2) * scale
  const seat = new THREE.Group()

  if (selectedTierId && selectedTierId !== tierId) {
    opacity = 0.3
  }

  seat.position.set(...position)

  const tableRadius = 2.5 * radius

  const [triangle] =
    group.seatShape === 'rect'
      ? drawRect(
          {
            width: group.width,
            height: group.height
          },
          {
            color: seatColor,
            position: seat.position,
            scale,
            opacity,
            sceneMeshes
          }
        )
      : drawTableCircle(
          seat.position,
          isExternalLink ? COLORS.AVAILABLE : seatColor,
          tableRadius,
          group,
          opacity,
          sceneMeshes
        )

  if (rotation) {
    triangle.rotateOnAxis(
      new THREE.Vector3(0, 0, 1),
      (rotation * Math.PI) / 180
    )
  }

  // calculate center coordinates of the group and draw label
  const labelText = group.label || ''
  const labelPosition = [
    center_x + group.x * scale + group.label_x * scale,
    center_y - group.y * scale + group.label_y * scale,
    0
  ]
  drawLabel(
    labelText,
    {
      font: fonts.openSans,
      color: 'black',
      position: labelPosition,
      center_x,
      center_y,
      scale,
      seat_circle_radius,
      scene,
      size: 12
    },
    textGeometries,
    materials
  )

  if (type === SEAT_TYPES.WHEELCHAIR) {
    drawIcon('\uF193', {
      font: fonts.fontAwesome,
      color: 'black',
      position,
      center_x,
      center_y,
      scale,
      seat_circle_radius,
      scene: seat,
      size: (group.width * scale) / 2,
      width: group.width,
      height: group.height,
      rotation
    })
  }
  seat.hasHandlers = true
  seat.add(triangle)
  sceneMeshes.push(seat)

  seat.isRequestPending = false
  seat.objectType = OBJECT_TYPES.SEAT
  seat.color = seatColor
  seat.opacity = opacity
  seat.disabled = disabled
  seat.allowColorUpdateByStatus = true
  seat.radius = radius
  seat.seatId = seatId
  seat.tierId = tierId
  seat.status = SEAT_STATUSES.BOOKED
  seat.sectionLabel = sectionLabel
  seat.rowLabel = rowLabel
  seat.label = label
  seat.seatType = type
  seat.isExternalLink = isExternalLink
  seat.seatExternalLink = seatExternalLink

  seat.material = triangle.obj.material // this is necessary for showing color in label

  seat.changeBorderColor = (color) => {
    triangle.material = getLineMaterial({ color }, materials)
  }

  seat.mouseOver = seat.disabled ? null : events.onMouseOver
  seat.mouseOut = seat.disabled ? null : events.onMouseOut

  seat.changeBorderColorToDefault = () => {
    seat.changeBorderColor(COLORS.BOOKED)
  }

  seat.changeColor = (color, opacity = 1) => {
    seat.changeBorderColor(color)

    triangle.material = getSeatMaterial({ color, opacity }, materials)
    triangle.obj.material = getSeatMaterial({ color, opacity }, materials)
    seat.changeBorderColorToDefault = () => {
      seat.changeBorderColor(color)
    }
    seat.material = triangle.obj.material
  }

  const makeAvailable = () => {
    seat.status = SEAT_STATUSES.AVAILABLE
  }

  const makeHold = () => {
    seat.status = SEAT_STATUSES.HOLD
  }

  const makeReserved = () => {
    seat.status = SEAT_STATUSES.RESERVED
  }

  const makeBooked = () => {
    seat.status = SEAT_STATUSES.BOOKED
  }

  const makeBlocked = () => {
    seat.status = SEAT_STATUSES.BLOCKED
  }

  const makeReservedByMe = () => {
    window.localStorage.setItem('tierId', tierId)
    seat.status = SEAT_STATUSES.RESERVED_BY_ME
  }

  seat.setColorByStatus = () => {
    if (!seat.allowColorUpdateByStatus || seat.isExternalLink) {
      return
    }

    switch (seat.status) {
      case SEAT_STATUSES.AVAILABLE: {
        seat.changeColor(color)
        break
      }
      case SEAT_STATUSES.HOLD: {
        seat.changeColor(COLORS.HOLD)
        break
      }
      case SEAT_STATUSES.RESERVED_BY_ME: {
        seat.changeColor(COLORS.RESERVED_BY_ME)
        break
      }
      case SEAT_STATUSES.RESERVED: {
        seat.changeColor(COLORS.RESERVED)
        break
      }
      case SEAT_STATUSES.BOOKED: {
        seat.changeColor(COLORS.BOOKED)
        break
      }
      case SEAT_STATUSES.BLOCKED: {
        seat.changeColor(COLORS.BLOCKED)
        break
      }
    }
  }

  seat.changeOpacity = (opacity, color) => {
    seat.changeColor(color, opacity)
  }

  seat.makeDisabled = () => {
    if (seat.disabled) {
      return
    }
    seat.disabled = true
    seat.mouseOver = null
    seat.mouseOut = null
    seat.opacity = 0.3
    seat.changeOpacity(0.3, seat.color)
  }

  seat.makeActive = () => {
    if (!seat.disabled) {
      return
    }
    seat.disabled = false
    seat.mouseOver = events.onMouseOver
    seat.mouseOut = events.onMouseOut
    seat.opacity = 1
    seat.changeOpacity(1, seat.color)
  }

  const select = () => {
    triangle.material = getLineMaterial(
      {
        color: '#575656',
        linewidth: 5,
        opacity: seat.opacity
      },
      materials
    )
    seat.changeBorderColor = () => null
  }

  const unselect = () => {
    triangle.material = getLineMaterial(
      { color: '#000', opacity: seat.opacity },
      materials
    )
    seat.changeBorderColor = (color) => {
      triangle.material = getLineMaterial(
        { color, opacity: seat.opacity },
        materials
      )
    }
    seat.changeBorderColorToDefault()
  }

  seat.select = select
  seat.unselect = unselect

  seat.unsetLoading = () => null
  seat.setLoading = () => {
    if (window.isMobile) {
      // is mobile
      const label = drawIcon('\uF110', {
        font: fonts.fontAwesome,
        color: 'grey',
        position: [seat.position.x, seat.position.y, seat.position.z],
        center_x: 0,
        center_y: 0,
        scale,
        seat_circle_radius,
        scene: seat,
        size: seat_circle_radius * scale,
        width: group.width,
        height: group.height,
        rotation
      })
      seat.unsetLoading = () => seat.remove(label)
    }
  }

  seat.statusUpdate = (status) => {
    if (seat.status !== status) {
      switch (status) {
        case SEAT_STATUSES.PERMANENT_RESERVED:
        case SEAT_STATUSES.AVAILABLE: {
          makeAvailable()
          seat.unsetLoading()
          break
        }
        case SEAT_STATUSES.HOLD: {
          makeHold()
          seat.unsetLoading()
          break
        }
        case SEAT_STATUSES.RESERVED_BY_ME: {
          makeReservedByMe()
          seat.unsetLoading()
          break
        }
        case SEAT_STATUSES.RESERVED: {
          makeReserved()
          seat.unsetLoading()
          break
        }
        case SEAT_STATUSES.BOOKED: {
          makeBooked()
          seat.unsetLoading()
          break
        }
        case SEAT_STATUSES.BLOCKED: {
          makeBlocked()
          seat.unsetLoading()
          break
        }
      }

      seat.setColorByStatus()
    }
  }

  seats[seatId] = seat
  seat.changeOpacity(opacity, seatColor)

  parent.add(seat)
}
